import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useScmConnectionMutations } from '@/api/hooks';
import type { ScmConnection, ScmConnectionTestResult } from '@/api/schemas';
import { showMutationError } from '@/lib/mutationError';
import { useCan, useFeature, type Can } from '@/lib/permissions';
import { scmBlockedReason, scmTestToast } from './scmConnectionModel';
import { ReplaceScmTokenDialog, ScmConnectionSheet } from './ScmConnectionSheet';

export interface ScmConnectionActions {
  /** Connecting, testing and re-tokening a workspace need `admin`. */
  can: Can;
  /** Why "Connect" is held back (this Hangar cannot store tokens or serve source control yet), if so. */
  blocked: string | undefined;
  connect: () => void;
  replaceToken: (connection: ScmConnection) => void;
  runTest: (connection: ScmConnection) => Promise<void>;
  testing: string | null;
  results: Record<string, ScmConnectionTestResult>;
  /** The connect sheet and the replace-token dialog; render once. */
  overlays: ReactNode;
}

/** "Test connection": the workspace being tested and the last result per workspace. */
function useConnectionTest() {
  const { test } = useScmConnectionMutations();
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ScmConnectionTestResult>>({});
  const runTest = async (c: ScmConnection) => {
    setTesting(c.id);
    try {
      const r = await test.mutateAsync({ id: c.id });
      setResults((s) => ({ ...s, [c.id]: r }));
      const t = scmTestToast(c, r);
      if (t.ok) toast.success(t.title, { description: t.description });
      else toast.error(t.title, { description: t.description });
    } catch (e) {
      showMutationError(e, 'Could not test the connection');
    } finally {
      setTesting(null);
    }
  };
  return { testing, results, runTest };
}

/** Connect, test and re-token Bitbucket workspaces: Integrations and Getting started's step 1. */
export function useScmConnectionActions(): ScmConnectionActions {
  const can = useCan('admin');
  const blocked = scmBlockedReason(useFeature('sourceControl'), useFeature('credentials'));
  const [connecting, setConnecting] = useState(false);
  const [replacing, setReplacing] = useState<ScmConnection | null>(null);
  const { testing, results, runTest } = useConnectionTest();
  const overlays = (
    <>
      <ScmConnectionSheet open={connecting} onOpenChange={setConnecting} />
      <ReplaceScmTokenDialog connection={replacing} onOpenChange={(o) => (o ? undefined : setReplacing(null))} />
    </>
  );
  return { can, blocked, connect: () => setConnecting(true), replaceToken: setReplacing, runTest, testing, results, overlays };
}
